import { DELETED_ACCOUNT_NAME } from "@/utils/deletedAccountMessages";

const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9_-]{2,63}$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;
const MAX_PARTICIPANT_PREVIEW = 4;
const MAX_THREAD_PARTICIPANTS = 150;

const fail = () => {
  throw new TypeError("The Messages conversation presentation is invalid.");
};

const boundedName = (value) => {
  const name = typeof value === "string" ? value.trim() : "";
  if (name.length < 1 || name.length > 160 || CONTROL_CHARACTERS.test(name)) {
    fail();
  }
  return name;
};

const normalizeParticipant = (value) => {
  if (
    !value ||
    typeof value !== "object" ||
    !PUBLIC_IDENTIFIER_PATTERN.test(value.publicIdentifier) ||
    typeof value.isSelf !== "boolean"
  ) {
    fail();
  }
  const avatarAssetId = value.avatarAssetId ?? null;
  if (avatarAssetId !== null && !SHA256_PATTERN.test(avatarAssetId)) fail();
  return Object.freeze({
    publicIdentifier: value.publicIdentifier,
    displayName: boundedName(value.displayName),
    avatarAssetId,
    isSelf: value.isSelf,
  });
};

export const groupConversationName = (participants, participantCount) => {
  const names = participants.map((participant) => participant.displayName);
  const otherCount = participantCount - 1;
  if (otherCount <= 2) return names.join(" and ");
  return `${names.slice(0, 2).join(", ")} and ${otherCount - 2} more`;
};

export function conversationPresentationFromThread(thread) {
  if (
    !thread ||
    typeof thread !== "object" ||
    !SHA256_PATTERN.test(thread.threadId) ||
    !Number.isSafeInteger(thread.revision) ||
    thread.revision < 1 ||
    !Array.isArray(thread.participants) ||
    thread.participants.length < 1 ||
    thread.participants.length > MAX_PARTICIPANT_PREVIEW ||
    !Number.isSafeInteger(thread.participantCount) ||
    thread.participantCount < thread.participants.length ||
    thread.participantCount > MAX_THREAD_PARTICIPANTS
  ) {
    fail();
  }
  const participants = Object.freeze(
    thread.participants.map(normalizeParticipant),
  );
  if (participants.filter((participant) => participant.isSelf).length !== 1) {
    fail();
  }
  const others = Object.freeze(
    participants.filter((participant) => !participant.isSelf),
  );
  const isGroupConversation = thread.participantCount > 2;
  const directParticipant = isGroupConversation ? null : others[0];
  // A direct conversation whose other participant's account was deleted.
  const partnerDeleted = !isGroupConversation && !directParticipant;
  return Object.freeze({
    uid: directParticipant?.publicIdentifier || thread.threadId,
    publicIdentifier: directParticipant?.publicIdentifier || null,
    displayName: partnerDeleted
      ? DELETED_ACCOUNT_NAME
      : directParticipant?.displayName ||
        groupConversationName(others, thread.participantCount),
    partnerDeleted,
    avatarAssetId: directParticipant?.avatarAssetId || null,
    threadId: thread.threadId,
    threadRevision: thread.revision,
    participants,
    participantCount: thread.participantCount,
    isGroupConversation,
    isPrivateMessagePresentation: true,
  });
}

export function normalizeConversationPresentation(value) {
  if (
    !value ||
    value.isPrivateMessagePresentation !== true ||
    typeof value.isGroupConversation !== "boolean" ||
    !SHA256_PATTERN.test(value.threadId) ||
    !Number.isSafeInteger(value.threadRevision) ||
    value.threadRevision < 1 ||
    !Array.isArray(value.participants) ||
    !Number.isSafeInteger(value.participantCount)
  ) {
    fail();
  }
  return conversationPresentationFromThread({
    threadId: value.threadId,
    revision: value.threadRevision,
    participants: value.participants,
    participantCount: value.participantCount,
  });
}
